import type { TemplateDoc, TemplateId } from '@content-scheduler/shared/render';
import { useEffect, type ComponentType } from 'react';
import { Announcement } from './announcement/Announcement';
import { Event } from './event/Event';
import { Fundraiser } from './fundraiser/Fundraiser';
import { Sale } from './sale/Sale';
import { useDismissed } from './dismiss';
import parts from './parts/parts.module.css';
import { loadFontPairing, themeStyle } from './theme';
import type { RenderContext, TemplateProps } from './types';

type Renderer<Id extends TemplateId> = {
    component: ComponentType<TemplateProps<Id>>;
    /** Single-row layouts get tighter corner rounding. */
    slimVariants: string[];
};

// Every template must have a renderer; adding one to shared/ without it fails to compile.
const RENDERERS: { [Id in TemplateId]: Renderer<Id> } = {
    announcement: { component: Announcement, slimVariants: ['bar'] },
    event: { component: Event, slimVariants: [] },
    sale: { component: Sale, slimVariants: ['ribbon'] },
    fundraiser: { component: Fundraiser, slimVariants: [] },
};

/** Renders a design with its theme. Returns null once a visitor dismisses it. */
export function TemplateView({ doc, ctx }: { doc: TemplateDoc; ctx: RenderContext }) {
    const { fontPairing } = doc.theme;
    useEffect(() => loadFontPairing(fontPairing), [fontPairing]);

    const { dismiss: settings } = doc.fields;
    const [dismissed, dismiss] = useDismissed(ctx.contentKey, settings.rememberDays, ctx.mode);
    const renderer = RENDERERS[doc.templateId] as Renderer<TemplateId>;
    if (dismissed) return null;

    const Component = renderer.component;
    return (
        <div
            className={parts.root}
            style={themeStyle(doc.theme)}
            data-template={doc.templateId}
            data-variant={doc.variant}
            data-slim={renderer.slimVariants.includes(doc.variant) ? '' : undefined}
        >
            <Component doc={doc as never} ctx={ctx} onDismiss={settings.enabled ? dismiss : null} />
        </div>
    );
}
